"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/utils/utils";

type Dictionaries = Record<string, Record<string, string>>;

type I18nContextValue = {
  locale: string;
  setLocale: (locale: string) => void;
  dictionaries: Dictionaries;
  t: (key: string, params?: Record<string, string | number>, fallback?: string) => string;
};

const I18nContext = React.createContext<I18nContextValue | null>(null);

function interpolate(template: string, params?: Record<string, string | number>) {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, k: string) =>
    params[k] !== undefined ? String(params[k]) : `{${k}}`
  );
}

function normalize(key: string) {
  return key.trim();
}

export const defaultDictionaries: Dictionaries = {
  en: {
    Home: "Home",
    Configure: "Configure",
  },
  es: {
    Home: "Inicio",
    Configure: "Configurar",
  },
};

export function I18nProvider({
  children,
  locale: localeProp,
  dictionaries = defaultDictionaries,
  storageKey = "app.locale",
}: {
  children: React.ReactNode;
  locale?: string;
  dictionaries?: Dictionaries;
  storageKey?: string;
}) {
  const [locale, setLocale] = React.useState<string>(() => {
    if (localeProp) return localeProp;
    if (typeof window !== "undefined") {
      const saved = window.localStorage.getItem(storageKey);
      if (saved) return saved;
      if (navigator?.language) return navigator.language.split("-")[0];
    }
    return "en";
  });

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(storageKey, locale);
    }
  }, [locale, storageKey]);

  const t = React.useCallback(
    (key: string, params?: Record<string, string | number>, fallback?: string) => {
      const k = normalize(key);
      const dict = dictionaries[locale] || {};
      const raw =
        dict[k] ??
        // Try case-insensitive fallback
        Object.entries(dict).find(([dk]) => dk.toLowerCase() === k.toLowerCase())?.[1] ??
        fallback ??
        key;
      return interpolate(raw, params);
    },
    [dictionaries, locale]
  );

  const value = React.useMemo<I18nContextValue>(
    () => ({ locale, setLocale, dictionaries, t }),
    [locale, dictionaries, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = React.useContext(I18nContext);
  if (!ctx) {
    // Provide a safe no-op fallback so Text can be used without provider
    return {
      locale: "en",
      setLocale: () => {},
      dictionaries: defaultDictionaries,
      t: (key: string, params?: Record<string, string | number>, fallback?: string) =>
        interpolate(fallback ?? key, params),
    } as I18nContextValue;
  }
  return ctx;
}

type TextProps<T extends React.ElementType = "span"> = {
  as?: T;
  asChild?: boolean;
  className?: string;
  t?: string; // key to translate; if omitted and children is string, it will be used as key
  params?: Record<string, string | number>;
  fallback?: string;
  children?: React.ReactNode;
} & Omit<React.ComponentPropsWithoutRef<T>, "children" | "className"> & {
    className?: string;
  };

/**
 * Internationalized Text component.
 * - Accepts Tailwind/utility classes via className
 * - Looks up the provided key (t) or string children in the active dictionary
 * - Supports simple interpolation: "Hello {name}"
 */
export function Text<T extends React.ElementType = "span">({
  as,
  asChild,
  className,
  t: keyProp,
  params,
  fallback,
  children,
  ...rest
}: TextProps<T>) {
  const Comp: any = asChild ? Slot : (as || "p");
  const { t } = useI18n();

  const keyFromChildren =
    typeof children === "string" || typeof children === "number"
      ? String(children)
      : undefined;

  const content = t(keyProp ?? keyFromChildren ?? "", params, fallback ?? keyFromChildren);

  return (
    <Comp className={cn(className)} {...rest}>
      {content}
    </Comp>
  );
}

export default Text;